import { inject, observer } from 'mobx-react';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Alert } from 'reactstrap';

import { get } from 'lodash';
import PropTypes from 'prop-types';
import Analytics from '../../../analytics/Analytics';
import useMaxQuantity from '../../../hooks/quantity/useMaxQuantity';
import Product from '../../../models/Product';
import FullProduct from '../../../models/product/FullProduct';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import CartStore from '../../../store/CartStore';
import ConfigStore from '../../../store/ConfigStore';
import ProposalStore from '../../../store/ProposalStore';
import RecurringOrderStore from '../../../store/RecurringOrderStore';
import ProductAvailabilityType from '../../../types/ProductAvailabilityType';
import ProductClass from '../../../types/ProductClass';
import RequestState from '../../../types/RequestState';
import {
  calculateNearestProductQuantityWithQuantityStep,
  roundWithPrecision,
} from '../../../util/number';
import AddToCart from '../../cart/AddToCart';
import CartIcon from '../../cart/CartIcon';
import SimpleAddToCart from '../../cart/SimpleAddToCart';
import ProductWatcherWidget from '../../product-watcher/ProductWatcherWidget';
import ProductRecurringOrder from '../ProductRecurringOrder';
import ProductSeasonButton from '../ProductSeasonButton';

export const DEFAULT_MAX_QUANTITY = 99999;

const createPayload = (id, qty) => {
  return {
    id,
    qty,
  };
};

const ProductAddToCart = ({
  accountStore,
  cartStore,
  configStore,
  recurringOrderStore,
  proposalStore,
  analytics,
  product,
  addToCartProduct,
  activeProductId,
  listId,
  listName,
  position,
  quantity,
  hideQuantity,
  ifWebStore,
  isValid,
  productPageContent,
  simple,
  withQuantityPicker,
  withRecurringOrder,
  withServices,
  onAddToCheckoutCartClick,
  onUpdateProductQuantity,
  scrollToServices,
  productListView,
}) => {
  const [loading, setLoading] = useState(false);
  const [addToCartError, setAddToCartError] = useState(null);
  const [messageOK, setMessageOK] = useState(null);
  const [getMaxQuantity] = useMaxQuantity({ accountStore, configStore, addToCartProduct });

  const withTax = accountStore.showPricesWithTax;
  if (product.season && !product.season.hasStarted()) {
    return <ProductSeasonButton season={product.season} />;
  }

  const ifShoppingCenter = configStore.siteConfig.isShoppingCenter;
  const ifBuyingAllowed =
    product.merchantInfo && product.merchantInfo.buyingAllowed;

  if (ifShoppingCenter && !ifBuyingAllowed) {
    return null;
  }

  if (activeProductId && product.isTemporarilyUnavailable(activeProductId)) {
    let productNameWithModel =
      addToCartProduct.name + ' ' + addToCartProduct.model;
    if (product.class === ProductClass.COLLECTION) {
      const collectionItem =
        product.collection.getItemWithProductId(activeProductId);
      if (collectionItem && collectionItem.name) {
        productNameWithModel =
          productNameWithModel + ' – ' + collectionItem.name;
      }
    }
    return !simple ? (
      <ProductWatcherWidget
        price={product.getPrice(withTax)}
        productName={productNameWithModel}
        activeProductId={activeProductId}
      />
    ) : null;
  }

  const onlyInShop =
    addToCartProduct.availability_type === ProductAvailabilityType.ONLY_IN_SHOP;

  const soldOut =
    activeProductId &&
    addToCartProduct.availability_type === ProductAvailabilityType.CLOSEOUT &&
    !product.hasEnoughStockAvailable(activeProductId);

  const hideAddToCart =
    onlyInShop ||
    (product.season && product.season.hasEnded()) ||
    (soldOut &&
      // Don't hide for collection or multi products to avoid confusion caused
      // by arriving to the page and not seeing an add to cart button.
      product.class !== ProductClass.COLLECTION &&
      product.class !== ProductClass.MULTI);

  if (hideAddToCart) {
    return null;
  }

  const disabled = !activeProductId || soldOut || !isValid;

  let disabledMessage;
  if (!activeProductId) {
    disabledMessage = (
      <FormattedMessage
        id="product.selectProductFirst"
        defaultMessage="First choose the product."
      />
    );
  } else if (
    (product.class !== ProductClass.COLLECTION ||
      product.class !== ProductClass.MULTI) &&
    soldOut
  ) {
    disabledMessage = (
      <FormattedMessage
        id="product.currentProductSoldOut"
        defaultMessage="The selected product has been sold out."
      />
    );
  } else if (soldOut) {
    disabledMessage = (
      <FormattedMessage
        id="product.hasBeenSoldOut"
        defaultMessage="This product has been sold out."
      />
    );
  } else if (!isValid) {
    disabledMessage = (
      <FormattedMessage
        id="product.checkingShippingValidity"
        defaultMessage="Product validity for the cart."
      />
    );
  }

  let tooltipMessage;
  const updateInProgress = cartStore.state === RequestState.LOADING;
  if (updateInProgress) {
    tooltipMessage = (
      <FormattedMessage
        id="cart.beingUpdated"
        defaultMessage="Cart is being updated"
      />
    );
  }

  let quantityAllowed =
    accountStore.isRetailer || configStore.productCard.showQuantitySelection;
  if (hideQuantity) {
    quantityAllowed = false;
  }


  const quantityStep = addToCartProduct.getQuantityStep(product);
  const buyingAllowed = !(
    configStore.recurringOrder.enabled &&
    accountStore.loggedIn &&
    addToCartProduct.hasOnlyRecurringOrder()
  );

  const handleAddToProposal = (quantityValue) => {
    if (proposalStore.state === RequestState.LOADING) {
      return;
    }

    if (activeProductId) {
      const quantityStep = addToCartProduct.getQuantityStep(product);
      const newQuantity = calculateNearestProductQuantityWithQuantityStep(
        quantityValue,
        quantityStep
      );
      const value = product.getPrice(withTax, activeProductId);

      proposalStore
        .addToProposal(createPayload(activeProductId, newQuantity))
        .then(() => {
          setLoading(false);
          setAddToCartError(null);
          setMessageOK(null);

            analytics.addToProposal(
              [
                {
                  product,
                  quantity: newQuantity,
                  activeProductId,
                  position,
                  itemListName: listName,
                  itemListId: listId,
                },
              ],
              roundWithPrecision(value * newQuantity, 4)
            );
        })
        .catch((e) => {
          setLoading(false);
          setAddToCartError(e);
          setMessageOK(null);
        });
    }
  };

  const handleAddToCartClick = (
    quantityValue,
    services = [],
    newRecurring = {}
  ) => {
    if (onAddToCheckoutCartClick) {
      onAddToCheckoutCartClick(addToCartProduct);
      return;
    }
    // If cartStore is already loading, don't do anything
    if (cartStore.state === RequestState.LOADING) {
      return;
    }

    if (activeProductId) {
      const quantityStep = addToCartProduct.getQuantityStep(product);
      const newQuantity = calculateNearestProductQuantityWithQuantityStep(
        quantityValue,
        quantityStep
      );
      const value = product.getPrice(withTax, activeProductId);
      const withActiveCategory = productListView !== true;
      setLoading(true);

      cartStore
        .addToCart(
          activeProductId,
          newQuantity,
          [],
          services,
          newRecurring,
          withActiveCategory
        )
        .then(() => {
          setLoading(false);
          setAddToCartError(null);
          setMessageOK(null);

          analytics.addToCart(
            [
              {
                product,
                quantity: newQuantity,
                activeProductId,
                position,
                itemListName: listName,
                itemListId: listId,
              },
            ],
            roundWithPrecision(value * newQuantity * quantityStep, 4)
          );

          analytics.cartStatus({
            cart: cartStore.cart,
          });
        })
        .catch((e) => {
          setLoading(false);
          setAddToCartError(e);
          setMessageOK(null);
        });
    }
  };

  const handleAddToOrderClick = (orderId, quantityValue, cycle) => {
    // If cartStore is already loading, don't do anything
    if (recurringOrderStore.state === RequestState.LOADING) {
      return;
    }
    if (activeProductId) {
      const quantityStep = addToCartProduct.getQuantityStep(product);
      const newQuantity = calculateNearestProductQuantityWithQuantityStep(
        quantityValue,
        quantityStep
      );
      const value = product.getPrice(withTax, activeProductId);

      setLoading(true);

      const orderProduct = Object.assign(
        {
          quantity: newQuantity,
          extendedId: activeProductId,
        },
        cycle
      );

      recurringOrderStore
        .addProduct(orderId, orderProduct)
        .then(() => {
          setLoading(false);
          setMessageOK(true);

          analytics.addToCart(
            [
              {
                product,
                quantity: quantityValue,
                activeProductId,
                position,
                itemListName: listName,
                itemListId: listId,
              },
            ],
            roundWithPrecision(value * newQuantity * quantityStep, 4)
          );
          analytics.cartStatus({
            cart: cartStore.cart,
          });
        })
        .catch((e) => {
          setLoading(false);
          setMessageOK(false);
        });
    }
  };

  const handleRecurringToCart = (newRecurring) => {
    handleAddToCartClick(quantity, [], newRecurring);
  };

  const handleRecurringToOrder = (orderId, newRecurring) => {
    handleAddToOrderClick(orderId, quantity, newRecurring);
  };

  const updateQuantity = (quantity) => {
    if (onUpdateProductQuantity) {
      onUpdateProductQuantity(quantity);
    }
  };

  const renderError = () => {
    if (!addToCartError) {
      return null;
    }

    let errors = get(addToCartError, 'response.data.errors');

    if (!errors) {
      return;
    }

    if (errors) {
      return (
        <Alert className="ProductAddToCart__errors" color="danger">
          {Object.values(errors).map((message) => (
            <div key={message}>{message}</div>
          ))}
        </Alert>
      );
    }

    return <UnexpectedError />;
  };

  const renderOrderMessage = () => {
    return messageOK ? (
      <Alert className="ProductAddToCart__errors" color="success">
        <FormattedMessage
          id="order.productAddedSentence"
          defaultMessage="Product added to order"
        />
      </Alert>
    ) : (
      <Alert className="ProductAddToCart__errors" color="danger">
        <FormattedMessage
          id="order.productAddFailedSentence"
          defaultMessage="Product cannot be added for order"
        />
      </Alert>
    );
  };

  let content = null;

  if (!simple) {
    content = (
      <>
        <div className="ProductAddToCart ProductAddToCart--normal">
          {buyingAllowed && (
            <AddToCart
              key={addToCartProduct.id}
              activeProductId={activeProductId}
              notAllowed={updateInProgress}
              disabled={disabled}
              tooltipMessage={disabledMessage || tooltipMessage}
              onAddToCartClick={handleAddToCartClick}
              onAddToProposalClick={handleAddToProposal}
              maxQuantity={getMaxQuantity()}
              quantityStep={quantityStep}
              unit={addToCartProduct.stock_unit}
              loading={loading}
              withTax={withTax}
              product={product}
              onUpdateProductQuantity={updateQuantity}
              quantity={quantity}
              isProposal={configStore.siteConfig.isOnlyProposal}
              withServices={withServices}
              withQuantityPicker={withQuantityPicker}
              scrollToServices={scrollToServices}
              productPageContent={productPageContent}
            />
          )}
        </div>
        {configStore.siteConfig.isHybrid && (
          <div className="ProductAddToCart ProductAddToCart--proposal">
            <AddToCart
              key={addToCartProduct.id}
              activeProductId={activeProductId}
              notAllowed={updateInProgress}
              disabled={disabled}
              tooltipMessage={disabledMessage || tooltipMessage}
              onAddToCartClick={handleAddToCartClick}
              onAddToProposalClick={handleAddToProposal}
              maxQuantity={getMaxQuantity()}
              quantityStep={quantityStep}
              unit={addToCartProduct.stock_unit}
              loading={loading}
              withTax={withTax}
              product={product}
              onUpdateProductQuantity={updateQuantity}
              quantity={quantity}
              isProposal={true}
              withQuantityPicker={withQuantityPicker}
              withServices={false}
              scrollToServices={scrollToServices}
              productPageContent={productPageContent}
            />
          </div>
        )}
        {addToCartError && renderError()}
        {withRecurringOrder && (
          <ProductRecurringOrder
            product={product}
            withTax={withTax}
            onAddToCartClick={handleRecurringToCart}
            onAddToOrderClick={handleRecurringToOrder}
            disabled={disabled}
            loading={loading}
          />
        )}
        {withRecurringOrder && messageOK !== null && renderOrderMessage()}
      </>
    );
  } else if (simple && !disabled && buyingAllowed) {
    content = (
      <>
        {quantityAllowed ? (
          <SimpleAddToCart
            notAllowed={updateInProgress}
            loading={loading}
            maxQuantity={getMaxQuantity()}
            quantityStep={quantityStep}
            onAddToCartClick={handleAddToCartClick}
            onAddToProposalClick={handleAddToProposal}
            unit={addToCartProduct.stock_unit}
            tooltipMessage={disabledMessage || tooltipMessage}
            disabled={disabled}
            onUpdateProductQuantity={updateQuantity}
            quantity={quantity}
            ifWebStore={ifWebStore}
          />
        ) : (
          ifWebStore && (
            <CartIcon
              notAllowed={updateInProgress}
              onClick={() => handleAddToCartClick(quantityStep)}
              onProposalClick={() => handleAddToProposal(quantityStep)}
              loading={loading}
              tooltipMessage={tooltipMessage}
            />
          )
        )}
        {addToCartError && renderError()}
      </>
    );
  }

  return content;
};

ProductAddToCart.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  recurringOrderStore: modelOf(RecurringOrderStore).isRequired,
  proposalStore: modelOf(ProposalStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  product: PropTypes.oneOfType([
    modelOf(Product).isRequired,
    modelOf(FullProduct).isRequired,
  ]),
  addToCartProduct: PropTypes.object.isRequired,
  activeProductId: PropTypes.string,
  listId: PropTypes.string,
  listName: PropTypes.string,
  position: PropTypes.number,
  quantity: PropTypes.number,
  hideQuantity: PropTypes.bool,
  ifWebStore: PropTypes.bool,
  isValid: PropTypes.bool,
  productPageContent: PropTypes.bool,
  simple: PropTypes.bool,
  withQuantityPicker: PropTypes.bool,
  withRecurringOrder: PropTypes.bool,
  withServices: PropTypes.bool,
  onAddToCheckoutCartClick: PropTypes.func,
  onUpdateProductQuantity: PropTypes.func,
  scrollToServices: PropTypes.func,
};

ProductAddToCart.defaultProps = {
  simple: false,
  hideQuantity: false,
  ifWebStore: true,
  withQuantityPicker: true,
  isValid: true,
};

export default inject(
  'accountStore',
  'cartStore',
  'configStore',
  'recurringOrderStore',
  'analytics',
  'recurringOrderStore',
  'proposalStore'
)(observer(ProductAddToCart));
